import { useCallback, useState } from "react";
import { apiDelete } from "../lib/apiClient.js";
import { makeDefaultDeps } from "../commands/deps.js";
import { sharedAccessLost } from "../lib/forecast/reducer.js";
import { beginIntent } from "../lib/telemetry/instrument";
import { intentArgs } from "../lib/telemetry/uiIntents.js";
import { toast } from "../lib/ui/toast.js";
import { ERROR_TEXT } from "../lib/forecast/runErrorText.js";

// The store singleton, built once at module load — NOT a `dispatchRun` passed in
// as an argument. Taking run-store handles as hook arguments is what the
// ratchet's `hooksTakingRunArgs` counter exists to drive to zero (it caught this
// hook doing it), and the reason is that the store is a singleton: threading a
// handle through props makes it look like an injected dependency that a caller
// could vary, when there is only ever one. Same idiom as
// useSharedForecasts/useBootForecasts.
const cmdDeps = makeDefaultDeps();

/**
 * "Revoke my access to this forecast" — the recipient's counterpart to the
 * owner's Delete.
 *
 * The row is dropped from the shared lane on SUCCESS ONLY, unlike the optimistic
 * writes elsewhere in this app. The reason is the asymmetry of being wrong: an
 * optimistic cadence that fails leaves a slider in the wrong place, while an
 * optimistic removal that fails makes a forecast vanish from someone's list and
 * come back on the next reload with no explanation. So the confirmation comes
 * first and the row goes after.
 *
 * @param {{ onDone?: (forecastId: string) => void }} [args]
 */
export function useRevokeMyAccess({ onDone } = {}) {
  const [revokingId, setRevokingId] = useState(null);

  const revokeMyAccess = useCallback(
    async (row) => {
      // `forecastId` is the pg UUID; the route is keyed that way for the same
      // reason the cadence route is — a client_id is unique only within one user
      // and cannot name a row belonging to someone else.
      const uuid = row?.forecastId;
      const runId = row?.id;
      if (!uuid || !runId) return false;
      setRevokingId(runId);
      const intent = beginIntent(
        "share.leave",
        { forecast_id: runId },
        { origin: "forecast-settings.revoke-access", surface: "forecast-settings" },
      );
      let res;
      try {
        res = await apiDelete(
          `/api/forecasts/${encodeURIComponent(uuid)}/access`,
          ...intentArgs(intent),
        );
      } catch (e) {
        intent.end("error");
        setRevokingId(null);
        console.warn(`[useRevokeMyAccess] DELETE access for ${uuid} errored:`, e);
        toast(ERROR_TEXT.offline, { kind: "error" });
        return false;
      }
      intent.end(res.ok ? "ok" : "error");
      setRevokingId(null);
      if (!res.ok) {
        console.warn(`[useRevokeMyAccess] DELETE access for ${uuid} failed: ${res.status}`);
        toast("Couldn't remove this forecast from your list. Try again.", { kind: "error" });
        return false;
      }
      // Same store action a discovered revocation uses (`sharedAccessLost`): from
      // the list's point of view the two outcomes are identical — this forecast is
      // no longer yours to see — and one action means one code path keeping the
      // lane consistent.
      cmdDeps.store.dispatch(sharedAccessLost(runId));
      onDone?.(uuid);
      return true;
    },
    [onDone],
  );

  return { revokeMyAccess, revokingId };
}
